<template>
    <div @click="enlarge">
        <slot />

        <ReviewDialog
            :open="current !== null"
            panel-class="w-full max-w-5xl transform overflow-hidden rounded-lg bg-white p-4 text-left shadow-xl transition-all sm:p-6"
            @close="close"
        >
            <div class="absolute right-0 top-0 pr-4 pt-4">
                <button
                    type="button"
                    data-test="close-enlarged-photo"
                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    @click="close"
                >
                    <span class="sr-only">Close</span>
                    <XIcon class="h-6 w-6" aria-hidden="true" />
                </button>
            </div>

            <div class="sm:grid sm:grid-cols-5 sm:gap-6">
                <div class="text-center sm:col-span-3">
                    <!-- Shrink-wrapped around the image so the arrows sit over its
                         own edges rather than the column's, which a portrait photo
                         leaves a long way off. -->
                    <div class="relative inline-block">
                        <img
                            v-if="current"
                            data-test="enlarged-photo"
                            :src="current.url"
                            alt="Photo submitted with this review"
                            class="w-auto max-w-full rounded-md object-contain"
                            style="max-height: 70vh"
                        />

                        <template v-if="photos.length > 1">
                            <button
                                type="button"
                                data-test="previous-photo"
                                class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white bg-opacity-80 p-2 text-gray-700 shadow hover:bg-opacity-100 focus:outline-none"
                                @click="showPhotoAtOffset(-1)"
                            >
                                <span class="sr-only">
                                    See the photo before
                                </span>
                                <ChevronLeftIcon
                                    class="h-6 w-6"
                                    aria-hidden="true"
                                />
                            </button>

                            <button
                                type="button"
                                data-test="next-photo"
                                class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white bg-opacity-80 p-2 text-gray-700 shadow hover:bg-opacity-100 focus:outline-none"
                                @click="showPhotoAtOffset(1)"
                            >
                                <span class="sr-only">
                                    See the photo after
                                </span>
                                <ChevronRightIcon
                                    class="h-6 w-6"
                                    aria-hidden="true"
                                />
                            </button>
                        </template>
                    </div>
                </div>

                <div class="mt-4 pr-8 sm:col-span-2 sm:mt-0">
                    <!-- Interpolated from props rather than slotted as markup: the
                         page body is compiled as a Vue template, and a review
                         containing {{ … }} is evaluated wherever it is inlined. A
                         prop is a string by the time it arrives. -->
                    <div v-if="name" class="flex items-center">
                        <SvgVue
                            :class="[
                                avatarClass,
                                'h-6 w-6 shrink-0 rounded-full',
                            ]"
                            data-test="lightbox-avatar"
                            icon="avatar"
                            aria-hidden="true"
                        />

                        <p
                            data-test="lightbox-name"
                            class="ml-4 text-sm text-gray-900"
                        >
                            {{ name }}
                        </p>
                    </div>

                    <span
                        v-if="verifiedPurchase"
                        data-test="lightbox-verified-purchase"
                        class="mt-2 inline-flex items-center gap-x-1.5 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700"
                    >
                        <CheckIcon
                            class="size-3 shrink-0 fill-green-500"
                            aria-hidden="true"
                        />
                        Verified Purchase
                    </span>

                    <StarRating
                        v-if="rating"
                        data-test="lightbox-rating"
                        :rating="rating"
                        class="mt-2"
                    />

                    <h2
                        v-if="title"
                        data-test="lightbox-title"
                        class="mt-3 font-bold text-gray-900"
                    >
                        {{ title }}
                    </h2>

                    <p
                        v-if="body"
                        data-test="lightbox-body"
                        class="mt-3 text-gray-500"
                    >
                        {{ body }}
                    </p>

                    <div class="mt-4 flex flex-wrap gap-2">
                        <button
                            v-for="(photo, index) in photos"
                            :key="photo.url"
                            type="button"
                            data-test="lightbox-thumbnail"
                            class="shrink-0 focus:outline-none"
                            @click="enlarged = index"
                        >
                            <span class="sr-only">
                                See photo {{ index + 1 }} larger
                            </span>
                            <img
                                :src="photo.thumbnailUrl"
                                alt=""
                                :class="[
                                    index === enlarged
                                        ? 'border-nutreance-green'
                                        : 'border-transparent',
                                    'h-16 w-16 rounded-md border-2 object-cover',
                                ]"
                            />
                        </button>
                    </div>
                </div>
            </div>
        </ReviewDialog>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
    CheckIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    XIcon,
} from "@heroicons/vue/solid";
import SvgVue from "../SvgVue.vue";
import ReviewDialog from "./ReviewDialog.vue";
import StarRating from "./StarRating.vue";

const props = defineProps<{
    /**
     * Every photo the lightbox pages through — the large image and the thumbnail that selects it —
     * in the order the strip is laid out, which is the order `data-photo-index` counts in.
     */
    photos: { url: string; thumbnailUrl: string }[];
    /** The review the photos were submitted with, beside the photo the way Amazon shows it. */
    name?: string;
    rating?: number;
    title?: string;
    body?: string;
    verifiedPurchase?: boolean;
    /** The avatar's colour, which is the product's own — written by the caller the way the
     *  review cards write it, so Tailwind sees the same dynamic class in Blade either way. */
    avatarClass?: string;
}>();

// An index into `photos` rather than a photo of its own, so paging is arithmetic and the strip is
// the single source of what may be opened.
const enlarged = ref<number | null>(null);
const current = computed(() =>
    enlarged.value === null ? null : (props.photos[enlarged.value] ?? null),
);

/**
 * Open whichever thumbnail was clicked.
 *
 * Delegated from this wrapper rather than bound to each thumbnail, because the strip is rendered
 * by the Blade template inside the slot: server-side, so a crawler and a browser with no
 * JavaScript still see the photos, with the lightbox as the enhancement on top.
 */
function enlarge(event: MouseEvent): void {
    const thumbnail = (
        event.target as HTMLElement | null
    )?.closest<HTMLElement>("[data-photo-index]");

    if (thumbnail) {
        enlarged.value = Number(thumbnail.dataset.photoIndex);
    }
}

function close(): void {
    enlarged.value = null;
}

/**
 * Move the given number of photos along the strip, wrapping at either end.
 */
function showPhotoAtOffset(offset: number): void {
    const count = props.photos.length;

    enlarged.value = ((enlarged.value ?? 0) + offset + count) % count;
}
</script>
